import React from "react";
import { Document, Image, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import { commonStyles } from "../../Services/ReactPDFService.jsx";

// Executive Sidebar Cover Letter Template - Two-column layout with dark blue sidebar matching resume
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    fontFamily: "Roboto",
    backgroundColor: "#ffffff",
    padding: 0,
    flexDirection: "row",
  },
  // Sidebar (Left Column)
  sidebar: {
    width: "30%",
    backgroundColor: "#2d3e50",
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 15,
  },
  // Main Content (Right Column)
  mainContent: {
    width: "70%",
    backgroundColor: "#ffffff",
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 15,
  },
  // Profile Photo Section
  profilePhotoContainer: {
    alignItems: "center",
    marginBottom: 20,
  },
  profilePhotoWrapper: {
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 3,
    borderColor: "#ffffff",
    overflow: "hidden",
    backgroundColor: "#4a5568",
  },
  profilePhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  profilePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 3,
    borderColor: "#ffffff",
    backgroundColor: "#4a5568",
    justifyContent: "center",
    alignItems: "center",
  },
  profileInitials: {
    fontSize: 32,
    fontWeight: 700,
    color: "#ffffff",
  },
  // Sidebar Section Titles
  sidebarSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#4a5c6e",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  sidebarSectionContainer: {
    marginBottom: 25,
  },
  // Contact Items in Sidebar
  sidebarContactItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  sidebarContactIcon: {
    width: 12,
    height: 12,
    marginRight: 8,
    marginTop: 1,
  },
  sidebarContactText: {
    fontSize: 9,
    color: "#d1d5db",
    flex: 1,
    lineHeight: 1.4,
  },
  // Sidebar Name Display
  sidebarName: {
    fontSize: 14,
    fontWeight: 700,
    color: "#ffffff",
    textAlign: "center",
    marginBottom: 20,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  sidebarTitle: {
    fontSize: 10,
    color: "#9ca3af",
    textAlign: "center",
    marginBottom: 20,
  },
  // Main Content - Header
  mainHeaderContainer: {
    marginBottom: 25,
    paddingBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: "#2d3e50",
  },
  mainNameFirstName: {
    fontSize: 26,
    fontWeight: 700,
    color: "#2d3e50",
    textTransform: "uppercase",
    letterSpacing: 1.5,
    marginBottom: 15,
  },
  mainNameLastName: {
    fontSize: 26,
    fontWeight: 300,
    color: "#2d3e50",
    textTransform: "uppercase",
    letterSpacing: 1.5,
    marginBottom: 5,
  },
  // Date and recipient section
  dateSection: {
    marginBottom: 20,
  },
  dateText: {
    fontSize: 10,
    color: "#6b7280",
    marginBottom: 15,
  },
  recipientText: {
    fontSize: 10,
    color: "#4b5563",
    marginBottom: 3,
  },
  // Body content
  bodyText: {
    fontSize: 10,
    lineHeight: 1.8,
    color: "#4b5563",
    textAlign: "justify",
    marginBottom: 14,
  },
  // Signature section
  signatureSection: {
    marginTop: 20,
  },
  signatureText: {
    fontSize: 10,
    color: "#4b5563",
    marginBottom: 3,
  },
  signatureName: {
    fontSize: 11,
    fontWeight: 700,
    color: "#2d3e50",
    marginTop: 10,
  },
});

// Contact icon paths
const contactIcons = {
  phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  email: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  website: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  linkedin: "M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z",
};

const ExecutiveSidebarCoverLetter = ({ coverLetterData, resumeData, customStyles = {} }) => {
  const { header, generatedContent, profilePhoto } = coverLetterData || {};
  const { name, email, phone, location, website, linkedin, title } = header || {};

  // Get profile photo URL - check both coverLetterData and resumeData
  const profilePhotoUrl = profilePhoto?.url || resumeData?.profilePhotoUrl || resumeData?.profilePhoto?.url || resumeData?.personalInfo?.profilePhotoUrl || null;

  // Get initials for placeholder
  const getInitials = () => {
    if (!name) return "?";
    const parts = name.split(" ");
    const first = parts[0]?.charAt(0) || "";
    const last = parts[parts.length - 1]?.charAt(0) || "";
    return (first + last).toUpperCase();
  };

  // Split name into first and last
  const getFirstName = () => {
    if (!name) return "[First Name]";
    return name.split(" ")[0] || "";
  };

  const getLastName = () => {
    if (!name) return "[Last Name]";
    const parts = name.split(" ");
    return parts.slice(1).join(" ") || "";
  };

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { titleColor } = customizations;
    const customStyleOverrides = {};

    if (titleColor) {
      customStyleOverrides.mainNameFirstName = {
        ...baseStyles.mainNameFirstName,
        color: titleColor,
      };
      customStyleOverrides.mainNameLastName = {
        ...baseStyles.mainNameLastName,
        color: titleColor,
      };
      customStyleOverrides.mainHeaderContainer = {
        ...baseStyles.mainHeaderContainer,
        borderBottomColor: titleColor,
      };
      customStyleOverrides.sidebar = {
        ...baseStyles.sidebar,
        backgroundColor: titleColor,
      };
      customStyleOverrides.signatureName = {
        ...baseStyles.signatureName,
        color: titleColor,
      };
    }

    return { ...baseStyles, ...customStyleOverrides };
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Parse the generated content into paragraphs
  const paragraphs = generatedContent
    ? generatedContent.split("\n").filter((p) => p.trim().length > 0)
    : ["[Your cover letter content will appear here]"];

  // Render contact icon
  const renderContactIcon = (iconPath, color = "#9ca3af") => (
    <Svg style={mergedStyles.sidebarContactIcon} viewBox="0 0 24 24">
      <Path d={iconPath} fill={color} />
    </Svg>
  );

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page}>
        {/* SIDEBAR - Left Column */}
        <View style={mergedStyles.sidebar}>
          {/* Profile Photo */}
          <View style={mergedStyles.profilePhotoContainer}>
            {profilePhotoUrl ? (
              <View style={mergedStyles.profilePhotoWrapper}>
                <Image src={profilePhotoUrl} style={mergedStyles.profilePhoto} />
              </View>
            ) : (
              <View style={mergedStyles.profilePlaceholder}>
                <Text style={mergedStyles.profileInitials}>{getInitials()}</Text>
              </View>
            )}
          </View>

          {/* Name in Sidebar */}
          <Text style={mergedStyles.sidebarName}>{name || "[Your Name]"}</Text>
          {title && <Text style={mergedStyles.sidebarTitle}>{title}</Text>}

          {/* Contact Info in Sidebar */}
          <View style={mergedStyles.sidebarSectionContainer}>
            <Text style={mergedStyles.sidebarSectionTitle}>Contact</Text>
            {phone && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.phone)}
                <Text style={mergedStyles.sidebarContactText}>{phone}</Text>
              </View>
            )}
            {email && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.email)}
                <Text style={mergedStyles.sidebarContactText}>{email}</Text>
              </View>
            )}
            {location && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.location)}
                <Text style={mergedStyles.sidebarContactText}>{location}</Text>
              </View>
            )}
            {website && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.website)}
                <Text style={mergedStyles.sidebarContactText}>{website}</Text>
              </View>
            )}
            {linkedin && (
              <View style={mergedStyles.sidebarContactItem}>
                {renderContactIcon(contactIcons.linkedin)}
                <Text style={mergedStyles.sidebarContactText}>{linkedin}</Text>
              </View>
            )}
          </View>
        </View>

        {/* MAIN CONTENT - Right Column */}
        <View style={mergedStyles.mainContent}>
          {/* Header with Name */}
          <View style={mergedStyles.mainHeaderContainer}>
            <Text style={mergedStyles.mainNameFirstName}>{getFirstName()}</Text>
            <Text style={mergedStyles.mainNameLastName}>{getLastName()}</Text>
          </View>

          {/* Body Content */}
          {paragraphs.map((paragraph, index) => (
            <Text key={index} style={mergedStyles.bodyText}>
              {paragraph}
            </Text>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default ExecutiveSidebarCoverLetter;
